import { useEffect, useState, memo } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons";

import { DepartmentDTO } from "../../DTOs/DepartmentDTO";
import { getContrastTextColor } from "../../utilities/helpers/getContrastTextColor";
import useDebounce from "../../components/Hooks/useDebounce";

type StoppagesTableHeaderProps = {
  selectedStatus: string;
  setSelectedStatus: React.Dispatch<React.SetStateAction<string>>;
  selectedJobType: { value: number; label: string } | null;
  setSelectedJobType: React.Dispatch<React.SetStateAction<{ value: number; label: string } | null>>;
  machineFilter: string;
  onMachineFilterChange: (value: string) => void;
  selectedDepartment: DepartmentDTO | null;
  setSelectedDepartment: React.Dispatch<React.SetStateAction<DepartmentDTO | null>>;
  jobTypeOptions: { value: number; label: string }[];
  isJobTypeOptionsLoading: boolean;
  departmentOptions: DepartmentDTO[];
  isDepartmentOptionsLoading: boolean;
}

const STOPPAGE_STATUS_OPTIONS = ["Down & Up", "Down"];

const StoppagesTableHeader: React.FC<StoppagesTableHeaderProps> = memo(({
  selectedStatus,
  setSelectedStatus,
  selectedJobType,
  setSelectedJobType,
  machineFilter,
  onMachineFilterChange,
  selectedDepartment,
  setSelectedDepartment,
  jobTypeOptions,
  isJobTypeOptionsLoading,
  departmentOptions,
  isDepartmentOptionsLoading
}) => {
  const [machineInput, setMachineInput] = useState(machineFilter);
  const debouncedMachineInput = useDebounce(machineInput, 400);

  useEffect(() => {
    onMachineFilterChange(debouncedMachineInput);
  }, [debouncedMachineInput, onMachineFilterChange]);

  return (
    <div className="flex flex-col w-full items-center fade-up">
      <div className="container overflow-y-hidden">
        <div className="bg-theme-surface-2 px-2 py-3 short:py-1.5 border border-theme-table-border mb-4 short:mb-2 shadow text-theme-text">
          <div className="flex flex-col justify-center gap-2 md:gap-4 short:gap-1 md:flex-row md:flex-wrap md:justify-start">
            <div className="flex flex-col items-center justify-start gap-2">
              <label className="text-2xl short:hidden">Status</label>
              <Autocomplete
                classes={{ root: "w-full min-w-[12rem]" }}
                size="small"
                value={selectedStatus}
                options={STOPPAGE_STATUS_OPTIONS}
                onChange={(_, val) => {
                  if (!val) return;
                  else setSelectedStatus(val);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Status"
                  />
                )}
                renderOption={(props, option) =>
                  <li
                    {...props}
                    key={option}
                  >{option}</li>}
              />
            </div>

            <div className="flex flex-col items-center justify-start gap-2">
              <label className="text-2xl short:hidden">Job Type</label>
              <Autocomplete
                classes={{ root: 'w-full min-w-[12rem]' }}
                size="small"
                value={selectedJobType}
                options={jobTypeOptions}
                loading={isJobTypeOptionsLoading}
                onChange={(_, val) => setSelectedJobType(val)}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Job Type"
                  />
                )}
                renderOption={(props, option) =>
                  <li
                    {...props}
                    key={option.value}
                  >{option.label}</li>}
              />
            </div>

            <div className="flex flex-col items-center justify-start gap-2">
              <label className="text-2xl short:hidden">Machine</label>
              <TextField
                size="small"
                label="Machine"
                value={machineInput}
                onChange={(e) => setMachineInput(e.target.value)}
                className="min-w-[12rem] w-full"
                slotProps={{
                  input: {
                    endAdornment:
                      <FontAwesomeIcon
                        className={`transition ease-out ${machineInput.length > 0 ? 'opacity-100 cursor-pointer' : 'opacity-0'} text-theme-text`}
                        icon={faXmark}
                        onClick={() => setMachineInput("")}
                      />
                  }
                }}
              />
            </div>

            <div className="flex flex-col items-center justify-start gap-2">
              <label className="text-2xl short:hidden">Department</label>
              <Autocomplete
                classes={{ root: "w-full min-w-[12rem]" }}
                size="small"
                value={selectedDepartment}
                options={departmentOptions}
                loading={isDepartmentOptionsLoading}
                onChange={(_, val) => setSelectedDepartment(val)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.department ?? ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department"
                    slotProps={{
                      ...params.slotProps,
                      input: {
                        ...params.slotProps.input,
                        endAdornment: (
                          <>
                            {isDepartmentOptionsLoading && <FontAwesomeIcon icon={faSpinner} spin />}
                            {params.slotProps.input.endAdornment}
                          </>
                        )
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => <li
                  {...props}
                  key={option.id}
                  style={{
                    backgroundColor: option.hexColour,
                    color: getContrastTextColor(option.hexColour),
                    fontWeight: "bold"
                  }}
                >{option.department}</li>}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default StoppagesTableHeader;